import {DatePicker, Form, Select} from 'antd';
import styles from './usersFilter.module.scss';
import {depositStatus, profileCompletionEnum} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {countryISOs, GET_ALL_CITIES} from 'graphql/queries/trendingCountries';
import {debounce} from 'utils/helper';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import moment from 'moment';
import {
  USER_PORTFOLIO_WEIGHT_FILTER_OPTIONS,
  USER_RISK_PROFILE_FILTER_OPTIONS,
  USER_SEGMENT_FILTER_OPTIONS,
  usersFilterEnumOptions,
} from 'utils/constants';

/**
 * The Users filter panel.
 *
 * Sixteen fields used to run down the panel in one undifferentiated column,
 * with the column count changing row to row — two wide, then three narrow,
 * then two again — so nothing lined up and nothing was grouped. Six fields had
 * no placeholder at all, and three were labelled only by a tooltip, which put
 * what they actually matched behind a hover.
 *
 * The fields are the same and carry the same names; they are grouped under
 * headings on one consistent grid, every control says what "unset" means, and
 * the explanations are visible.
 *
 * ⚠️ The field names are the contract. `buildUserFilterInput` in
 * AdminUserList reads `startDate` / `endDate` / `walletTransactionStartDate` /
 * `walletTransactionEndDate` individually, so the date pairs are presented as
 * ranges but remain four separate fields — a RangePicker would hand
 * `handleFilter` an array under one key and both dates would vanish.
 */

const FORM_DATE_FIELDS = [
  'startDate',
  'endDate',
  'walletTransactionStartDate',
  'walletTransactionEndDate',
];

const MODAL_FORM_KEYS = [
  'nationality',
  'city',
  'country',
  'profileCompletion',
  'startDate',
  'endDate',
  'walletTransactionStartDate',
  'walletTransactionEndDate',
  'walletTransactionType',
  'depositStatus',
  'hasMadeReferral',
  'hasMadeDeposit',
  'hasMadeInvestment',
  'riskProfile',
  'userSegments',
  'portfolioWeight',
];

const mapSavedFiltersToFormValues = (saved = {}) => {
  const out = {};
  for (const key of MODAL_FORM_KEYS) {
    const raw = saved[key];
    if (raw === undefined || raw === null || raw === '') continue;
    if (FORM_DATE_FIELDS.includes(key)) {
      const m = moment.isMoment(raw) ? raw : moment(raw);
      if (m.isValid()) out[key] = m;
    } else {
      out[key] = raw;
    }
  }
  return out;
};

/** A titled group of fields. */
const Section = ({title, hint, children}) => (
  <section className={styles.section}>
    <div className={styles.sectionHead}>
      <h2>{title}</h2>
      {hint && <p>{hint}</p>}
    </div>
    <div className={styles.grid}>{children}</div>
  </section>
);

/**
 * Two date fields presented as one range.
 *
 * They stay separate form fields — see the note at the top of the file — but
 * read as a pair and refuse to cross: an end date before the start used to be
 * accepted, and returned an empty list with no explanation.
 */
const DateRange = ({form, startName, endName, startLabel, endLabel}) => {
  const start = Form.useWatch(startName, form);
  const end = Form.useWatch(endName, form);

  return (
    <div className={styles.rangeRow}>
      <Form.Item name={startName} label={startLabel}>
        <DatePicker
          className={styles.fieldWidth}
          allowClear
          placeholder="From"
          format={'YYYY-MM-DD HH:mm:ss'}
          disabledDate={current => (end ? current.isAfter(end, 'day') : false)}
          showTime={{defaultValue: moment('00:00', 'HH:mm:ss')}}
        />
      </Form.Item>
      <span className={styles.rangeArrow} aria-hidden="true">
        →
      </span>
      <Form.Item name={endName} label={endLabel}>
        <DatePicker
          className={styles.fieldWidth}
          allowClear
          placeholder="To"
          format={'YYYY-MM-DD HH:mm:ss'}
          disabledDate={current =>
            start ? current.isBefore(start, 'day') : false
          }
          showTime={{defaultValue: moment('00:00', 'HH:mm:ss')}}
        />
      </Form.Item>
    </div>
  );
};

const UsersFilter = ({
  handleClose,
  handleFilter,
  onReset,
  activePage,
  savedFilterValues = {},
}) => {
  const [cities, setCities] = useState([]);
  const [form] = Form.useForm();
  const savedFilterValuesRef = useRef(savedFilterValues);
  savedFilterValuesRef.current = savedFilterValues;

  const {data, loading} = useQuery(GET_ALL_COUNTRIES);
  const {
    refetch,
    networkStatus,
    loading: loadingCities,
  } = useQuery(GET_ALL_CITIES, {
    variables: {
      filter: {
        shouldPaginate: true,
        limit: 500,
      },
    },
    fetchPolicy: 'cache-first',
    onCompleted: data => {
      if (data?.getAllCities?.data) {
        setCities(data?.getAllCities?.data);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(
      mapSavedFiltersToFormValues(savedFilterValuesRef.current),
    );
  }, [activePage, form]);

  const onSearchCity = value => {
    refetch({
      filter: {
        search: value,
        limit: 500,
        shouldPaginate: true,
      },
    });
  };

  const optimizedVersion = useCallback(debounce(onSearchCity), []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h1>Filter users</h1>
          <p className={styles.headerHint}>
            Applies on top of the tab and the search box.
          </p>
        </div>
        <button
          type="button"
          className={styles.close}
          aria-label="Close filters"
          onClick={handleClose}>
          ×
        </button>
      </div>
      <Form
        className={styles.form}
        form={form}
        onFinish={handleFilter}
        layout="vertical">
        <div className={styles.scrollArea}>
          <Section title="Location">
            <Form.Item name="nationality" label="Nationality">
              <Select
                allowClear
                showSearch
                placeholder="Any nationality"
                loading={loadingCities}>
                {loading && (
                  <Select.Option key="Loading...">Loading...</Select.Option>
                )}
                {data?.getAllCountries?.map(data => (
                  <Select.Option key={data?.name}>
                    <div className={styles.allData}>
                      <img src={data?.icon} />
                      <span>{data?.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="city" label="City">
              <Select
                onSearch={value => optimizedVersion(value)}
                allowClear
                placeholder="Any city"
                filterOption={false}
                showSearch>
                <>
                  {cities &&
                    cities?.map(data => (
                      <Select.Option key={data?.city}>
                        {data?.city}
                      </Select.Option>
                    ))}
                  {(networkStatus === 2 || loadingCities) && (
                    <Select.Option key="Loading...">Loading...</Select.Option>
                  )}
                </>
              </Select>
            </Form.Item>
            <Form.Item
              name="country"
              label="Country of citizenship"
              extra="From the KYC document, not the profile.">
              <Select
                optionFilterProp="label"
                allowClear
                showSearch
                placeholder="Any country">
                {countryISOs.map(data => (
                  <Select.Option label={data.label} key={data.value}>
                    {data.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="profileCompletion"
              label="Profile completion"
              extra="How far through onboarding the account is.">
              <Select allowClear placeholder="Any stage">
                {profileCompletionEnum.map(data => (
                  <Select.Option label={data.label} key={data.value}>
                    {data.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Section>

          <Section
            title="Investor profile"
            hint="Each of these accepts more than one value.">
            <Form.Item name="riskProfile" label="Risk profile">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="All risk profiles">
                {USER_RISK_PROFILE_FILTER_OPTIONS.map(option => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="userSegments" label="User segment">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="All segments">
                {USER_SEGMENT_FILTER_OPTIONS.map(option => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="portfolioWeight" label="Portfolio weight">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="Any weighting">
                {USER_PORTFOLIO_WEIGHT_FILTER_OPTIONS.map(option => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Section>

          <Section title="Joined" hint="When the account was created.">
            <DateRange
              form={form}
              startName="startDate"
              endName="endDate"
              startLabel="Joined from"
              endLabel="Joined up to"
            />
          </Section>

          <Section
            title="Wallet activity"
            hint="Matches users with a wallet transaction in this window.">
            <DateRange
              form={form}
              startName="walletTransactionStartDate"
              endName="walletTransactionEndDate"
              startLabel="Transacted from"
              endLabel="Transacted up to"
            />
            <Form.Item name="walletTransactionType" label="Transaction type">
              <Select allowClear showSearch placeholder="Any type">
                <Select.Option key="DEPOSIT">Deposit</Select.Option>
                <Select.Option key="WITHDRAWAL">Withdrawal</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="depositStatus" label="Transaction status">
              <Select allowClear showSearch placeholder="Any status">
                {depositStatus.map(data => (
                  <Select.Option key={data.value}>{data.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Section>

          {/* These three were labelled by tooltip only, so what each one
              actually matched was hidden behind a hover on the label. */}
          <Section title="Has the user…">
            <Form.Item
              name="hasMadeReferral"
              label="Referred someone"
              extra="Invited at least one other user.">
              <Select allowClear placeholder="Either">
                {usersFilterEnumOptions.map(option => (
                  <Select.Option key={option.value}>
                    <span>{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="hasMadeDeposit"
              label="Deposited funds"
              extra="A successful deposit into their wallet.">
              <Select allowClear placeholder="Either">
                {usersFilterEnumOptions.map(option => (
                  <Select.Option key={option.value}>
                    <span>{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="hasMadeInvestment"
              label="Invested"
              extra="Bought into a stock or a startup.">
              <Select allowClear placeholder="Either">
                {usersFilterEnumOptions.map(option => (
                  <Select.Option key={option.value}>
                    <span>{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Section>
        </div>
        <div className={styles.footer}>
          {/* There was no way out of a filter from in here: the panel only
              ever added to the set, so clearing meant emptying every field by
              hand. */}
          <button
            type="button"
            className={styles.resetButton}
            onClick={() => {
              form.resetFields();
              onReset?.();
            }}>
            Reset all
          </button>
          <SubmitButton label="Apply filters" />
        </div>
      </Form>
    </div>
  );
};

export default UsersFilter;
